import { useState, useCallback, useRef, useEffect } from 'react';
import { SidebarTab, SidebarItem } from '../types/cockpit';
import { EnhancedDashboardTask, DashboardEmail, DashboardClient, PlannerTask } from '../types/api';
import LexAIService from '../services/api';

// Cache TTL (time-to-live) per tab type in milliseconds
const CACHE_TTL: Record<SidebarTab, number> = {
  tasks: 2 * 60 * 1000,     // 2 minutes - tasks change frequently
  emails: 5 * 60 * 1000,    // 5 minutes - moderate refresh needed
  clients: 30 * 60 * 1000,  // 30 minutes - clients rarely change
  deals: 15 * 60 * 1000,    // 15 minutes - deals change occasionally
  files: 10 * 60 * 1000     // 10 minutes - files moderate refresh
};

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface UseSidebarDataReturn {
  sidebarData: {
    clients: SidebarItem[];
    deals: SidebarItem[];
    files: SidebarItem[];
    tasks: SidebarItem[];
    emails: SidebarItem[];
  };
  fullTasksData: EnhancedDashboardTask[];
  fullEmailsData: DashboardEmail[];
  loading: boolean;
  loadSidebarData: (tab: SidebarTab) => Promise<void>;
  refreshTabData: (tab: SidebarTab) => Promise<void>;
}

export const useSidebarData = (): UseSidebarDataReturn => {
  const [sidebarData, setSidebarData] = useState<{
    clients: SidebarItem[];
    deals: SidebarItem[];
    files: SidebarItem[];
    tasks: SidebarItem[];
    emails: SidebarItem[];
  }>({
    clients: [],
    deals: [],
    files: [],
    tasks: [],
    emails: []
  });

  // Store full task and email data for the canvas
  const [fullTasksData, setFullTasksData] = useState<EnhancedDashboardTask[]>([]);
  const [fullEmailsData, setFullEmailsData] = useState<DashboardEmail[]>([]);
  const [loading, setLoading] = useState(false);

  // Cache timestamps to track when data was last fetched
  const cacheTimestamps = useRef<Record<SidebarTab, number>>({
    clients: 0,
    deals: 0,
    files: 0,
    tasks: 0,
    emails: 0
  });

  // Track in-flight requests to prevent duplicate API calls
  const pendingRequests = useRef<Record<SidebarTab, boolean>>({
    clients: false,
    deals: false,
    files: false,
    tasks: false,
    emails: false
  });

  // Check if cached data is still valid (even if empty)
  const isCacheValid = useCallback((tab: SidebarTab): boolean => {
    const lastFetch = cacheTimestamps.current[tab];
    if (lastFetch === 0) return false; // Never fetched
    const ttl = CACHE_TTL[tab];
    return Date.now() - lastFetch < ttl;
  }, []);

  const loadSidebarData = useCallback(async (tab: SidebarTab, forceRefresh = false) => {
    // Cache-first pattern: return if cache is valid (even if data is empty - prevents retry loops)
    if (!forceRefresh && isCacheValid(tab)) {
      return; // Use cached data (or empty result from recent fetch)
    }

    // Prevent duplicate in-flight requests for the same tab
    if (pendingRequests.current[tab]) {
      return;
    }

    pendingRequests.current[tab] = true;
    setLoading(true);

    try {
      let data: SidebarItem[] = [];

      // Use real API calls
      switch (tab) {
        case 'tasks':
          const tasks = await LexAIService.getDashboardTasks();
          setFullTasksData(tasks); // Store full task data
          data = tasks.map((task: EnhancedDashboardTask, index: number) => {
            // Format due date nicely
            const formatDueDate = (dueDate: string) => {
              try {
                const date = new Date(dueDate);
                const today = new Date();
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);

                // Reset time components for comparison
                today.setHours(0, 0, 0, 0);
                yesterday.setHours(0, 0, 0, 0);
                tomorrow.setHours(0, 0, 0, 0);
                const compareDate = new Date(date);
                compareDate.setHours(0, 0, 0, 0);

                if (compareDate.getTime() === today.getTime()) {
                  return 'Due today';
                } else if (compareDate.getTime() === yesterday.getTime()) {
                  return 'Overdue (yesterday)';
                } else if (compareDate < today) {
                  return `Overdue (${date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`;
                } else if (compareDate.getTime() === tomorrow.getTime()) {
                  return 'Due tomorrow';
                } else {
                  return `Due ${date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}`;
                }
              } catch (e) {
                return 'Due today';
              }
            };

            return {
              id: task.id || `task-${index}`,
              title: task.task,
              subtitle: formatDueDate(task.dueDate),
              lastModified: task.dueDate ? new Date(task.dueDate) : new Date(),
              type: 'task'
            };
          });
          break;

        case 'emails':
          const emails = await LexAIService.getDashboardEmails();
          setFullEmailsData(emails); // Store full email data
          data = emails.map((email: DashboardEmail, index: number) => ({
            id: `email-${index}`, // For now, use index-based ID since basic emails don't have Microsoft Graph IDs
            title: email.subject,
            subtitle: email.sender || 'Unknown Sender',
            lastModified: new Date(email.date),
            type: 'email'
          }));
          break;

        case 'clients':
          // Fetch individual people from client.md files in SharePoint sandbox
          // Returns people with their full details parsed from markdown
          const people = await LexAIService.getSandboxPeople();
          data = people.map((person: DashboardClient, index: number) => ({
            id: person.id || `person-${index}`,
            title: person.name || person.preferredName || 'Unknown',
            subtitle: person.hasSyncedData
              ? `${person.clientFolder} • ${person.deals?.length || 0} deal(s)`
              : `${person.clientFolder || 'Sandbox'} • Not synced`,
            lastModified: person.lastSync ? new Date(person.lastSync) : new Date(),
            type: 'client' as const,
            // Store full person data for PersonDetails component
            personData: person
          }));
          break;

        case 'deals':
          // Fetch real deals from Microsoft Planner
          // The "Home Loans" plan contains deals - can also fetch from multiple plans
          const plannerTasks = await LexAIService.getDashboardPlannerTasks('Home Loans');

          data = plannerTasks.map((task: PlannerTask) => ({
            id: task.id,
            title: task.title,
            subtitle: `${task.status} • ${task.planName}`,
            lastModified: task.createdDate ? new Date(task.createdDate) : new Date(),
            type: 'deal' as const,
            dealData: task  // Store full task data for opening
          }));
          break;

        case 'files':
          // Files are now handled by SharePointFileExplorer component
          data = [];
          break;
      }

      // Update cache timestamp BEFORE setting data to prevent race conditions
      cacheTimestamps.current[tab] = Date.now();
      setSidebarData(prev => ({ ...prev, [tab]: data }));
    } catch (error) {
      console.error(`Failed to load ${tab} data:`, error);
      // Still set cache timestamp on error to prevent retry loops
      cacheTimestamps.current[tab] = Date.now();
    } finally {
      pendingRequests.current[tab] = false;
      setLoading(false);
    }
  }, [isCacheValid]); // Removed sidebarData - was causing infinite loop

  // Force refresh bypasses cache
  const refreshTabData = useCallback((tab: SidebarTab) => {
    return loadSidebarData(tab, true);
  }, [loadSidebarData]);

  // Listen for sync completion events to invalidate sidebar cache
  useEffect(() => {
    const handleSyncComplete = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const tab = detail?.tab as SidebarTab;
      if (tab) {
        // Invalidate cache for the specified tab
        cacheTimestamps.current[tab] = 0;
        loadSidebarData(tab, true);
      }
    };
    window.addEventListener('lexai-sync-complete', handleSyncComplete);
    return () => window.removeEventListener('lexai-sync-complete', handleSyncComplete);
  }, [loadSidebarData]);

  return {
    sidebarData,
    fullTasksData,
    fullEmailsData,
    loading,
    loadSidebarData,
    refreshTabData
  };
};
